import React, { useEffect, useState } from 'react';
import { ClipboardCopy, Check, MessageCircle, Mail } from 'lucide-react';

const SECTIONS = [
    {
        title: 'You and your story',
        questions: [
            { id: 'name', short: true, label: 'Your full name, and the name you want to build the brand under', hint: 'Personal name, company name, or both.' },
            { id: 'story', label: 'Your story so far, in your own words', hint: 'Ventures, exits, pivots, the moments that shaped how you think. Rough is fine.' },
            { id: 'unique', label: 'What do you keep coming back to?', hint: 'Opinions, experiences or takes only you can share. The things you repeat in conversations.' }
        ]
    },
    {
        title: 'Business and offer',
        questions: [
            { id: 'sell', label: 'What exactly do you sell today, and who pays you?', hint: 'Include your ticket size and how clients currently find you.' },
            { id: 'offer', label: 'The one offer your profile should drive', hint: 'If it is not clear yet, list the 2 or 3 candidates you are weighing.' },
            { id: 'different', label: 'What makes you different from others selling something similar?', hint: 'Be specific. Quality is not an answer.' }
        ]
    },
    {
        title: 'Who we are targeting',
        questions: [
            { id: 'icp', label: 'Describe your dream customer', hint: 'Role, industry, geography, company size or revenue. One person, not a category.' },
            { id: 'where', label: 'Where does this person spend time online, and who do they already follow?', hint: 'Platforms, creators, communities, newsletters.' },
            { id: 'pain', label: 'What problem keeps them up at night that you can solve?', hint: 'In their words, not yours.' }
        ]
    },
    {
        title: 'Goals',
        questions: [
            { id: 'goal90', label: 'What does success look like in 90 days?', hint: 'Followers, leads, booked calls, revenue. Put numbers on it.' },
            { id: 'goal12', label: 'And at 12 months?', hint: 'Where should this brand have taken the business by then?' }
        ]
    },
    {
        title: 'Content and voice',
        questions: [
            { id: 'admire', label: '2 or 3 creators or pages you admire, with links', hint: 'And one line on why each.' },
            { id: 'hardnos', label: 'Hard nos', hint: 'Topics, words or associations you never want on your profile.' },
            { id: 'tone', short: true, label: 'Language and tone', hint: 'English, Hindi or mixed? Polished or raw? Formal or personal?' },
            { id: 'handles', label: 'Your current social handles and website links', hint: 'Instagram, LinkedIn, X, YouTube, website. Paste them all.' }
        ]
    },
    {
        title: 'Practical',
        questions: [
            { id: 'time', short: true, label: 'How much time can you realistically give per week?', hint: 'Approvals, plus one recording session for the clone.' },
            { id: 'assets', label: 'What do you already have?', hint: 'Footage, brand colors, logo, decks, testimonials, case studies.' },
            { id: 'anything', label: 'Anything else we should know?', hint: 'Constraints, deadlines, things that worry you.' }
        ]
    }
];

const ALL_QUESTIONS = SECTIONS.flatMap((s) => s.questions);
const STORAGE_KEY = 'socialseo-brand-questionnaire-v1';

export default function BrandQuestionnaire() {

    const [answers, setAnswers] = useState(() => {
        try {
            return JSON.parse(localStorage.getItem(STORAGE_KEY)) || {};
        } catch (e) {
            return {};
        }
    });
    const [copied, setCopied] = useState(false);

    useEffect(() => {
        document.title = "SocialSEO | Brand Questionnaire";
        window.scrollTo(0, 0);
    }, []);

    useEffect(() => {
        try {
            localStorage.setItem(STORAGE_KEY, JSON.stringify(answers));
        } catch (e) { /* storage unavailable, answers stay in memory */ }
    }, [answers]);

    const setAnswer = (id, value) => setAnswers((prev) => ({ ...prev, [id]: value }));

    const answeredCount = ALL_QUESTIONS.filter((q) => (answers[q.id] || '').trim()).length;

    const buildText = () => {
        const lines = ['SocialSEO Brand Questionnaire', ''];
        let n = 0;
        SECTIONS.forEach((section) => {
            lines.push('== ' + section.title + ' ==');
            section.questions.forEach((q) => {
                n += 1;
                lines.push(n + '. ' + q.label);
                lines.push((answers[q.id] || '').trim() || '(not answered)');
                lines.push('');
            });
        });
        return lines.join('\n');
    };

    const copyAnswers = async () => {
        const text = buildText();
        try {
            await navigator.clipboard.writeText(text);
        } catch (e) {
            const ta = document.createElement('textarea');
            ta.value = text;
            document.body.appendChild(ta);
            ta.select();
            document.execCommand('copy');
            document.body.removeChild(ta);
        }
        setCopied(true);
        setTimeout(() => setCopied(false), 2500);
    };

    const waHref = 'https://wa.me/919252560207?text=' + encodeURIComponent(buildText());
    const mailHref = 'mailto:suresh@socialseo.in?subject=' + encodeURIComponent('Brand questionnaire answers') + '&body=' + encodeURIComponent(buildText());

    let qNumber = 0;

    return (
        <div className="font-sans leading-relaxed text-white overflow-x-hidden selection:bg-indigo-500/30 pt-36 sm:pt-40 pb-20 relative z-10">

            {/* Floating Header */}
            <header className="fixed top-0 left-0 right-0 z-50 p-4 sm:p-6 flex justify-between items-center bg-transparent pointer-events-none">
                <div className="bg-white/10 backdrop-blur-lg border border-white/10 px-3 sm:px-4 py-2 rounded-full pointer-events-auto shadow-lg">
                    <span className="font-bold tracking-tight text-white text-sm sm:text-base">Social<span className="text-indigo-400">SEO</span></span>
                    <span className="text-white/30 mx-1.5 sm:mx-2">/</span>
                    <span className="text-white/70 font-medium text-xs sm:text-sm">Brand Questionnaire</span>
                </div>
                <div className="bg-indigo-500/20 backdrop-blur-lg border border-indigo-500/30 px-2.5 sm:px-3 py-1 rounded-full pointer-events-auto">
                    <span className="text-indigo-300 text-[10px] sm:text-xs font-bold tracking-widest uppercase">{answeredCount}/{ALL_QUESTIONS.length}</span>
                </div>
            </header>

            <main className="max-w-[760px] mx-auto px-6">

                {/* HERO */}
                <section className="text-center pt-8 sm:pt-14 mb-16 sm:mb-20">
                    <div className="text-indigo-300/70 text-[10px] sm:text-xs font-bold tracking-[0.3em] uppercase mb-8">
                        Step 01 · Before the strategy
                    </div>
                    <h1 className="text-4xl sm:text-5xl md:text-6xl font-black tracking-tight mb-6 bg-clip-text text-transparent bg-gradient-to-r from-white via-indigo-200 to-indigo-400 leading-[1.05] pb-2">
                        Tell us what<br />only you know.
                    </h1>
                    <p className="text-base sm:text-lg text-white/55 max-w-xl mx-auto leading-relaxed font-light">
                        About 20 minutes, in your own words. Answer it yourself, not with AI. The strategy we build is only as good as this input. Your answers save in this browser as you type.
                    </p>
                </section>

                {/* QUESTIONS */}
                <div className="space-y-14 sm:space-y-16">
                    {SECTIONS.map((section) => (
                        <section key={section.title}>
                            <div className="text-indigo-300/80 text-[10px] sm:text-xs font-bold tracking-[0.3em] uppercase mb-6">
                                {section.title}
                            </div>
                            <div className="space-y-5">
                                {section.questions.map((q) => {
                                    qNumber += 1;
                                    return (
                                        <div key={q.id} className="rounded-2xl border border-white/10 bg-white/5 backdrop-blur-sm p-5 sm:p-6">
                                            <div className="flex items-start gap-4 mb-4">
                                                <div className="text-lg sm:text-xl font-black text-indigo-400/80 shrink-0 tabular-nums">{String(qNumber).padStart(2, '0')}</div>
                                                <div>
                                                    <div className="text-sm sm:text-base font-bold text-white leading-snug">{q.label}</div>
                                                    <div className="text-xs sm:text-sm text-white/40 font-medium leading-snug mt-1">{q.hint}</div>
                                                </div>
                                            </div>
                                            {q.short ? (
                                                <input
                                                    type="text"
                                                    value={answers[q.id] || ''}
                                                    onChange={(e) => setAnswer(q.id, e.target.value)}
                                                    className="w-full rounded-xl bg-black/30 border border-white/10 focus:border-indigo-400/60 focus:outline-none px-4 py-3 text-sm text-white placeholder-white/25"
                                                    placeholder="Type here"
                                                />
                                            ) : (
                                                <textarea
                                                    rows={4}
                                                    value={answers[q.id] || ''}
                                                    onChange={(e) => setAnswer(q.id, e.target.value)}
                                                    className="w-full rounded-xl bg-black/30 border border-white/10 focus:border-indigo-400/60 focus:outline-none px-4 py-3 text-sm text-white placeholder-white/25 resize-y"
                                                    placeholder="Type here"
                                                />
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                        </section>
                    ))}
                </div>

                {/* SUBMIT */}
                <section className="text-center mt-20 sm:mt-24">
                    <h2 className="text-2xl sm:text-3xl font-black tracking-tight text-white leading-tight mb-4">
                        Done? Send it across.
                    </h2>
                    <p className="text-sm sm:text-base text-white/55 max-w-md mx-auto font-light leading-relaxed mb-8">
                        Copy your answers, then paste them to us on WhatsApp or email. Research starts the same day.
                    </p>
                    <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
                        <button
                            onClick={copyAnswers}
                            className="inline-flex items-center gap-2 bg-indigo-500 hover:bg-indigo-400 transition text-white font-bold px-7 py-3.5 rounded-full text-sm"
                        >
                            {copied ? <Check className="w-4 h-4" /> : <ClipboardCopy className="w-4 h-4" />}
                            {copied ? 'Copied' : 'Copy my answers'}
                        </button>
                        <a
                            href={waHref}
                            target="_blank"
                            rel="noreferrer"
                            className="inline-flex items-center gap-2 bg-white/10 hover:bg-white/15 border border-white/15 transition text-white font-bold px-7 py-3.5 rounded-full text-sm"
                        >
                            <MessageCircle className="w-4 h-4" />
                            Send on WhatsApp
                        </a>
                        <a
                            href={mailHref}
                            className="inline-flex items-center gap-2 bg-white/10 hover:bg-white/15 border border-white/15 transition text-white font-bold px-7 py-3.5 rounded-full text-sm"
                        >
                            <Mail className="w-4 h-4" />
                            Send by email
                        </a>
                    </div>
                    <p className="text-xs text-white/35 mt-6">{answeredCount} of {ALL_QUESTIONS.length} answered so far</p>
                </section>

                {/* FOOTER */}
                <footer className="text-center text-slate-500 text-xs sm:text-sm pt-20 mt-16 border-t border-white/5">
                    <div className="font-bold text-white/60 mb-3 tracking-wider">SocialSEO LLP</div>
                    <div>
                        <a href="mailto:suresh@socialseo.in" className="text-indigo-300/80 hover:text-indigo-200 transition">suresh@socialseo.in</a>
                    </div>
                </footer>

            </main>
        </div>
    );
}
